import { Badge, Button, Drawer, Skeleton, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { DOCUMENTS_QUERY_KEY, documentStateDescriptor } from '../documents/documents.api';
import {
  SUBSCRIPTION_STATUS_LIST,
  subscriptionsQueryKey,
} from '../subscriptions/subscriptions.api';
import {
  archiveCustomer,
  CUSTOMERS_QUERY_KEY,
  type Customer,
  type CustomerArchivePlan,
  customerArchivePlanQueryOptions,
  customerQueryKey,
  EMPTY_ARCHIVE_PLAN,
  unarchiveCustomer,
} from './customers.api';

/**
 * Arhivarea și dezarhivarea clientului, cu ce trebuie spus înainte.
 *
 * ## De ce sertar și nu un „Ești sigur?"
 *
 * Pentru că are ce să spună, și de la 2026-09-15 are **mult** mai mult: arhivarea nu mai e o
 * curățenie a listei, e închiderea relației. Clientul iese inactiv, serviciile în derulare se
 * reziliază, documentele nesemnate se anulează și linkul de pe telefonul clientului moare.
 * Un dialog de confirmare are loc de o întrebare, nu de un inventar.
 *
 * ## Arată tot, apoi cere confirmarea
 *
 * ⚠️ **Lista, nu numărul.** Înainte, cât arhivarea nu atingea nimic, un „are 2 abonamente
 * active" era destul — era un avertisment despre o stare care rămânea. Acum e o listă de
 * lucruri care se închid ireversibil, iar cine apasă trebuie să vadă *care* servicii, *ce*
 * documente și *ce* link. Rezilierea nu se desface, nici măcar dezarhivând.
 *
 * Planul se cere **la deschiderea sertarului**, proaspăt de fiecare dată: între două apăsări
 * se poate semna un act de pe telefon. Ce se scrie la sfârșit e planul întors de server — ce
 * s-a închis **de fapt** — nu cel arătat aici.
 *
 * Vezi `docs/wiki/domains/companies-and-clients.md#arhivarea`.
 */
export function ArchiveCustomerDrawer({
  customer,
  onClose,
}: {
  customer: Customer | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isArchived = customer?.archivedAt != null;

  /**
   * Ce s-ar închide, cerut de la server.
   *
   * Nu se compune din interogările tabelor („Servicii", „Documente"): serverul aplică
   * aceleași filtre pe care le va aplica și la scriere, iar o a doua implementare în browser
   * ar fi putut arăta altceva decât se întâmplă. La dezarhivare nu se cere — nu se închide
   * nimic.
   */
  const plan = useQuery({
    ...customerArchivePlanQueryOptions(customer?.id ?? ''),
    enabled: customer !== null && !isArchived,
  });

  const closing: CustomerArchivePlan = plan.data ?? EMPTY_ARCHIVE_PLAN;
  const nothingToClose =
    closing.subscriptions.length === 0 &&
    closing.documents.length === 0 &&
    closing.signing.length === 0;

  const mutation = useMutation({
    mutationFn: () => {
      if (!customer) {
        throw new Error('Niciun client de arhivat.');
      }
      return isArchived ? unarchiveCustomer(customer.id) : archiveCustomer(customer.id);
    },
    onSuccess: async (saved) => {
      toast.success(isArchived ? 'Client dezarhivat' : 'Client arhivat', {
        description: isArchived ? undefined : closedSummary(saved.plan),
      });
      // Și fișa, și lista: clientul tocmai arhivat trebuie să dispară din listă la
      // întoarcere, altfel comutatorul „Arată și arhivații" pare că nu face nimic.
      //
      // ⚠️ **Și tot ce s-a închis.** Cu cascada, tabul „Servicii" și registrul de documente
      // ale clientului arată stări vechi până la o reîncărcare cu mâna — `CUSTOMERS_QUERY_KEY`
      // nu le atinge. De-asta se golește tot ce ține de client.
      await queryClient.invalidateQueries({ queryKey: customerQueryKey(saved.customer.id) });
      await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: subscriptionsQueryKey(saved.customer.id) });
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError
          ? error.message
          : isArchived
            ? 'Clientul nu a putut fi dezarhivat.'
            : 'Clientul nu a putut fi arhivat.',
      );
    },
  });

  return (
    <Drawer open={customer !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {customer
              ? `${isArchived ? 'Dezarhivează' : 'Arhivează'} clientul ${customer.nickname}`
              : ''}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          {isArchived ? (
            <>
              <Text size="small">
                Clientul revine în lista obișnuită
                {customer?.archivedAt
                  ? `. Arhivat la ${formatDateTime(customer.archivedAt)}.`
                  : '.'}
              </Text>
              {/*
                Spus înainte, nu descoperit după: cine dezarhivează se așteaptă, pe bună
                dreptate, să găsească clientul cum l-a lăsat.
              */}
              <Text size="small" className="text-ui-fg-subtle">
                Serviciile reziliate și documentele anulate la arhivare <b>rămân așa</b>, iar
                clientul rămâne inactiv. Dezarhivarea îl aduce doar înapoi în listă.
              </Text>
            </>
          ) : (
            <>
              <Text size="small" className="text-ui-fg-error">
                Clientul devine <b>inactiv</b> și iese din lista de clienți.
              </Text>

              {plan.isPending ? (
                <div className="flex flex-col gap-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : plan.isError ? (
                // Nu se ascunde butonul: serverul recalculează oricum tot, sub blocare. Ce
                // se pierde e inventarul, și asta se spune.
                <Text size="small" className="text-ui-fg-error">
                  Lista lucrurilor care se închid nu a putut fi citită. Arhivarea le închide oricum
                  — dacă vrei să le vezi întâi, închide sertarul și încearcă din nou.
                </Text>
              ) : nothingToClose ? (
                <Text size="small" className="text-ui-fg-subtle">
                  Clientul n-are servicii în derulare, documente nesemnate sau link de semnare
                  activ.
                </Text>
              ) : (
                <>
                  <Section
                    title="Servicii care se reziliază"
                    hint="Definitivă: un serviciu reziliat nu se reactivează."
                    items={closing.subscriptions.map((subscription) => ({
                      key: subscription.id,
                      label: subscription.name,
                      detail: subscription.address,
                      badge: subscriptionStatusLabel(subscription.status),
                    }))}
                  />

                  <Section
                    title="Linkuri de semnare care se invalidează"
                    hint="Linkul nu mai deschide nimic, nici de pe telefonul clientului."
                    items={closing.signing.map((session) => ({
                      key: session.documentId,
                      label: documentLabel(session),
                      detail: session.sentToEmail ?? 'trimis ca link, fără e-mail',
                      badge: `expira ${formatDateTime(session.expiresAt)}`,
                    }))}
                  />

                  {/*
                    ⚠️ **Două feluri de act sub același titlu** ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)):
                    ciornele se **șterg**, actele emise se **anulează**. Titlul le spune pe
                    amândouă, iar rândurile se deosebesc singure — ce n-are număr e ciornă.
                    Două secțiuni ar fi însemnat două liste goale pe majoritatea clienților.
                  */}
                  <Section
                    title="Documente în curs care se închid"
                    hint="Ciornele se șterg. Actele cu număr se anulează, iar numerele rămân consumate. Documentele semnate nu se ating."
                    items={closing.documents.map((document) => ({
                      key: document.id,
                      label: documentLabel(document),
                      detail: null,
                      badge:
                        document.documentNumber === null
                          ? 'ciornă — se șterge'
                          : documentStateLabel(document.documentState),
                    }))}
                  />
                </>
              )}
            </>
          )}
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            variant={isArchived ? 'primary' : 'danger'}
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            // ⚠️ Blocat cât se încarcă planul: altfel un dublu clic rapid ar arhiva înainte
            // ca operatorul să apuce să vadă ce închide.
            disabled={mutation.isPending || (!isArchived && plan.isPending)}
          >
            {isArchived ? 'Dezarhivează' : 'Arhivează clientul'}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

interface SectionItem {
  readonly key: string;
  readonly label: string;
  readonly detail: string | null;
  readonly badge: string;
}

/** O grupă din inventar. Absentă când n-are ce arăta — un titlu gol e zgomot, nu informație. */
function Section({
  title,
  hint,
  items,
}: {
  title: string;
  hint: string;
  items: readonly SectionItem[];
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-2">
      <Text size="small" weight="plus">
        {title}
      </Text>
      <ul className="flex flex-col gap-y-2">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-center justify-between gap-x-2 rounded-md border border-ui-border-base px-3 py-2"
          >
            <div className="flex flex-col">
              <Text size="small">{item.label}</Text>
              {item.detail ? (
                <Text size="xsmall" className="text-ui-fg-subtle">
                  {item.detail}
                </Text>
              ) : null}
            </div>
            <Badge size="2xsmall">{item.badge}</Badge>
          </li>
        ))}
      </ul>
      <Text size="xsmall" className="text-ui-fg-subtle">
        {hint}
      </Text>
    </div>
  );
}

/**
 * Starea abonamentului, ca etichetă — căutată după număr, nu după tip.
 *
 * Planul duce `status` ca `number`, dinadins (vezi `customerArchivePlanSchema`): o stare
 * necunoscută nu trebuie să pice la `parse`. Aici cade pe „Necunoscut", nu aruncă.
 */
function subscriptionStatusLabel(status: number): string {
  return SUBSCRIPTION_STATUS_LIST.find((item) => item.value === status)?.label ?? 'Necunoscut';
}

function documentStateLabel(state: number): string {
  // `documentStateDescriptor` cade singur pe `Ciornă` pentru orice valoare necunoscută.
  return documentStateDescriptor(state as Parameters<typeof documentStateDescriptor>[0]).label;
}

/**
 * Cum se numește un act în listele de aici — **cu numărul lui, dacă are**
 * ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
 *
 * O ciornă n-are număr, iar un „nr. null" pe ecranul care cere confirmarea arhivării ar fi
 * arătat ca un defect exact în clipa în care operatorul citește ce se închide.
 */
function documentLabel(document: {
  documentTypeName: string;
  documentNumber: number | null;
}): string {
  return document.documentNumber === null
    ? document.documentTypeName
    : `${document.documentTypeName} nr. ${document.documentNumber}`;
}

/**
 * Ce s-a închis, într-o singură frază — pentru toast.
 *
 * Numărul, nu lista: toastul se citește din colțul ecranului, iar inventarul l-a văzut deja
 * cine a apăsat. Ce adaugă e confirmarea că serverul a făcut exact atât.
 */
function closedSummary(plan: CustomerArchivePlan): string | undefined {
  const parts = [
    countLabel(plan.subscriptions.length, 'serviciu reziliat', 'servicii reziliate'),
    countLabel(plan.signing.length, 'link invalidat', 'linkuri invalidate'),
    countLabel(plan.documents.length, 'document anulat', 'documente anulate'),
  ].filter((part) => part !== null);

  return parts.length === 0 ? undefined : parts.join(' · ');
}

function countLabel(count: number, singular: string, plural: string): string | null {
  if (count === 0) {
    return null;
  }

  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}
